import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { QUERY_KEYS, STALE_TIME } from '@/libs/constants'
import type { HookParams, StoreData, StoreListParams, Stores } from '@/libs/interfaces'
import { storesApi } from '@/services/api'

export function useStores({ params, enabled }: HookParams<StoreListParams>) {
  return useQuery<Partial<Stores[]>>({
    queryKey: QUERY_KEYS.stores.list(params),
    queryFn: () => storesApi.list(params),
    enabled: enabled ?? true,
    staleTime: STALE_TIME['24Hours'], //24 hours
  })
}

export function useStoresByID({ params, enabled }: HookParams<Stores>) {
  return useQuery<StoreData>({
    queryKey: QUERY_KEYS.stores.list(params),
    queryFn: () => storesApi.byID(params),
    enabled: enabled ?? true,
    staleTime: STALE_TIME['24Hours'], //24 hours
  })
}
export function useInfiniteStores({ params, enabled }: HookParams<StoreListParams>) {
  return useInfiniteQuery({
    queryKey: QUERY_KEYS.stores.infinite(params),
    queryFn: ({ pageParam }) =>
      storesApi.listCursor({
        ...params,
        cursor: pageParam,
      }),
    getNextPageParam: (lastPage) => lastPage.pageInfo.next || undefined,
    getPreviousPageParam: (firstPage) => firstPage.pageInfo.prev || undefined,
    enabled: enabled ?? true,
    initialPageParam: undefined as string | undefined,
    staleTime: STALE_TIME['24Hours'], //24 Hours
  })
}

export function useStoresCreate() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: storesApi.create,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.stores.all,
      })
    },
    onError: () => {
      toast.error(t('common.error'))
    },
  })
}

export function useStoresDelete() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: storesApi.delete,
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: QUERY_KEYS.stores.all })
    },
    onError: () => {
      toast.error(t('common.error'))
    },
  })
}

export function useStoreUpdate() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: storesApi.update,
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: QUERY_KEYS.stores.all })
    },
    onError: () => {
      toast.error(t('common.error'))
    },
  })
}
